import { useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import CTA from '../../home/components/CTA';
import { industryServicesData } from '../../../mocks/industryServices';
import { useSEO, getServiceSchema, getBreadcrumbSchema, getFAQSchema, combineSchemas } from '../../../utils/seo';

export default function PlumbingPage() {
  const serviceSchema = getServiceSchema(
    'Digital Marketing for Plumbing Contractors',
    'Specialized marketing strategies designed to help plumbing contractors capture emergency calls and build a steady stream of service requests.'
  );

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Industries', url: '/industries' },
    { name: 'Plumbing', url: '/industries/plumbing' }
  ]);

  const faqSchema = getFAQSchema([
    {
      question: 'How can digital marketing help my plumbing business?',
      answer: 'Digital marketing helps plumbing contractors capture emergency calls 24/7, promote high-value remodeling projects, fill schedules with routine service calls, dominate local search results, and build trust through review management.'
    },
    {
      question: 'What marketing services do you offer for plumbers?',
      answer: 'We offer comprehensive digital marketing services including SEO, Google Ads, web design, social media marketing, reputation management, and AI integration specifically tailored for plumbing contractors.'
    },
    {
      question: 'How quickly can I see results from plumbing marketing?',
      answer: 'While SEO takes 3-6 months for significant results, Google Ads and Local Services Ads can generate leads within days. We focus on both immediate lead generation and long-term organic growth.'
    },
    {
      question: 'Do you market all types of plumbing services?',
      answer: 'Yes, we market all plumbing services including emergency repairs, drain cleaning, water heater installation, pipe repair, bathroom remodeling, fixture installation, sewer line services, and commercial plumbing.'
    }
  ]);

  useSEO({
    title: 'Digital Marketing for Plumbing Contractors | Plumber Marketing Services',
    description: 'Specialized digital marketing for plumbing contractors. Capture emergency calls and generate steady service requests with SEO, Google Ads, web design, and social media marketing.',
    keywords: 'plumbing contractor marketing, plumber marketing, plumbing SEO, plumber lead generation, plumbing contractor advertising',
    canonical: '/industries/plumbing',
    schema: combineSchemas(serviceSchema, breadcrumbSchema, faqSchema)
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600 rounded-2xl mb-6">
              <i className="ri-drop-line text-4xl text-white"></i>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Digital Marketing for Plumbing Contractors
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Specialized marketing strategies designed to help plumbing contractors capture emergency calls and build a steady stream of service requests.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={scrollToContact} className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer">
                Get Started <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Services We Market For Plumbing Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand your business and know how to attract customers for all your services
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industryServicesData.plumbing.map((service, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-all border-l-4 border-red-600">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-red-600 text-xl flex-shrink-0 mt-1"></i>
                  <h3 className="font-heading text-lg font-semibold text-black">{service}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
                Why Plumbing Contractors Choose Us
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We specialize in marketing for plumbing contractors and understand the emergency nature of many plumbing calls.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-phone-line', text: 'Capture emergency plumbing calls 24/7' },
                  { icon: 'ri-money-dollar-circle-line', text: 'Promote high-value remodeling projects' },
                  { icon: 'ri-tools-line', text: 'Fill schedule with routine service calls' },
                  { icon: 'ri-map-pin-line', text: 'Dominate local search results' },
                  { icon: 'ri-star-line', text: 'Build trust with review management' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-white`}></i>
                    </div>
                    <p className="text-lg text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/38b13435392b4bf501c026ce2fc78a03.jpg"
                alt="Plumbing Contractor"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}
